import cs from "classnames";
import React from "react";
import {
	GoChevronDown,
	GoChevronLeft,
	GoChevronRight,
	GoChevronUp,
	GoPlus
} from "react-icons/go";
import { MdBarChart, MdHistory, MdInfoOutline } from "react-icons/md";
import { defineMessages, FormattedMessage, useIntl } from "react-intl";
import { NavLink, useLocation } from "react-router-dom";

import {
	invitationsIcon,
	patientsIcon,
	signOutIcon,
	usersIcon
} from "src/common/components/Icons";
import {
	useFormatInitials,
	useFormatName
} from "src/common/hooks/useFormatName";
import { useSidebarState } from "src/common/hooks/useSidebarState";
import { HasPermission, Permission, useAuthState } from "src/features/auth";
import { OrgModLink, useOrgModParams } from "src/features/orgmod";
import { genericMessages } from "src/messages/generic";
import { OrgMod } from "src/views/sidebar/OrgMod";

import "./Sidebar.css";

const messages = defineMessages({
	subtitle: {
		id: "sidebar.subtitle",
		defaultMessage: "Strukturált adatgyűjtés"
	},
	patients: {
		id: "sidebar.patient_list",
		defaultMessage: "Páciensek"
	},
	addPatient: {
		id: "sidebar.add_patient",
		defaultMessage: "Páciens felvétele"
	},
	users: {
		id: "sidebar.user_list",
		defaultMessage: "Felhasználók"
	},
	invitations: {
		id: "sidebar.invitation_list",
		defaultMessage: "Meghívók"
	},
	sendInvitation: {
		id: "sidebar.add_invitation",
		defaultMessage: "Meghívó küldése"
	},
	stats: {
		id: "sidebar.stats",
		defaultMessage: "Statisztikák"
	},
	signOut: {
		id: "sidebar.sign_out",
		defaultMessage: "Kijelentkezés"
	}
});

const IS_COLLAPSED_KEY = "isSidebarCollapsed";

export const Sidebar: React.FunctionComponent = () => {
	const { isMobile, isCollapsed, onToggle } = useSidebarState(
		IS_COLLAPSED_KEY,
		true
	);
	return (
		<div className={cs("sidebar", { "sidebar--collapsed": isCollapsed })}>
			<button className="sidebar__toggle" onClick={onToggle}>
				{isMobile ? (
					isCollapsed ? (
						<GoChevronDown />
					) : (
						<GoChevronUp />
					)
				) : isCollapsed ? (
					<GoChevronRight />
				) : (
					<GoChevronLeft />
				)}
			</button>
			<div className="sidebar__header">
				<a
					href="https://semmelweis.hu/informatika"
					target="_blank"
					rel="noreferrer"
				>
					<div className="sidebar__title">
						<h1>TIMM</h1>
						<h2>
							<FormattedMessage {...messages.subtitle} />
						</h2>
					</div>
				</a>
			</div>
			<div className="sidebar__body">
				<div className="sidebar__orgmod">
					<OrgMod />
				</div>
				<nav className="sidebar__nav">
					<Patients />
					<HasPermission permission={Permission.UsersRW}>
						<Users />
					</HasPermission>
					<HasPermission permission={Permission.UsersRW}>
						<Invitations />
					</HasPermission>
					<HasPermission permission={Permission.Stats}>
						<Stats />
					</HasPermission>
					<Information />
				</nav>
				<nav className="sidebar__nav">
					<Changes />
					<UserProfile />
					<SignOut />
				</nav>
			</div>
		</div>
	);
};

const Patients: React.FunctionComponent = () => {
	const { formatMessage } = useIntl();
	return (
		<NavItem
			activePath={"/patients"}
			left={{
				to: "/patients/list",
				icon: patientsIcon,
				text: formatMessage(messages.patients)
			}}
			right={{
				to: "/patients/new",
				icon: <GoPlus />,
				text: formatMessage(messages.addPatient)
			}}
		/>
	);
};

const Users: React.FunctionComponent = () => {
	const { formatMessage } = useIntl();
	return (
		<NavItem
			left={{
				to: "/users/list",
				icon: usersIcon,
				text: formatMessage(messages.users)
			}}
		/>
	);
};

const Invitations: React.FunctionComponent = () => {
	const { formatMessage } = useIntl();
	return (
		<NavItem
			left={{
				to: "/invitations/list",
				icon: invitationsIcon,
				text: formatMessage(messages.invitations)
			}}
			right={{
				to: "/invitations/new",
				icon: <GoPlus />,
				text: formatMessage(messages.sendInvitation)
			}}
		/>
	);
};

const Stats: React.FunctionComponent = () => {
	const { formatMessage } = useIntl();
	return (
		<NavItem
			left={{
				to: "/stats",
				icon: <MdBarChart />,
				text: formatMessage(messages.stats)
			}}
		/>
	);
};

const Information: React.FunctionComponent = () => {
	const { mod } = useOrgModParams();
	const { formatMessage } = useIntl();
	if (mod !== "ktx" && mod !== "kd") {
		return null;
	}
	return (
		<NavItem
			left={{
				to: "/information",
				icon: <MdInfoOutline />,
				text: formatMessage(genericMessages.information)
			}}
		/>
	);
};

const Changes: React.FunctionComponent = () => {
	const { formatMessage } = useIntl();
	return (
		<NavItem
			left={{
				to: "/changelog",
				icon: <MdHistory />,
				text: formatMessage(genericMessages.changes)
			}}
		/>
	);
};

const UserProfile: React.FunctionComponent = () => {
	const { user } = useAuthState();
	const formatName = useFormatName();
	const formatInitials = useFormatInitials();
	if (user === null) {
		return null;
	}
	const name = formatName(user);
	const initials = formatInitials(user);
	return (
		<NavItem
			left={{
				to: "/users/profile",
				icon: (
					<div className="nav-item__user-initials" title={name}>
						{initials}
					</div>
				),
				text: name
			}}
		/>
	);
};

const SignOut: React.FunctionComponent = () => {
	const { formatMessage } = useIntl();
	return (
		<NavItem
			useBare={true}
			left={{
				to: `/sign-out`,
				icon: signOutIcon,
				text: formatMessage(messages.signOut)
			}}
		/>
	);
};

interface NavItemProps {
	activePath?: string;
	left: {
		to: string;
		icon: React.ReactNode;
		text: string;
	};
	right?: {
		to: string;
		icon: React.ReactNode;
		text: string;
	};
	useBare?: boolean;
}

const NavItem: React.FunctionComponent<NavItemProps> = ({
	activePath,
	left,
	right,
	useBare
}) => {
	const { pathname } = useLocation();
	const isActive = React.useMemo(() => {
		const pathnameWithoutOrgmod = pathname.replace(/^(\/\w+){2}/, "");
		if (activePath !== undefined) {
			return pathnameWithoutOrgmod.startsWith(activePath);
		}
		if (pathnameWithoutOrgmod.startsWith(left.to)) {
			return true;
		}
		if (right !== undefined) {
			return pathnameWithoutOrgmod.startsWith(right.to);
		}
		return false;
	}, [pathname, activePath, left, right]);
	return (
		<div className={cs("nav-item", { "nav-item--active": isActive })}>
			<NavItemLink
				useBare={useBare}
				className={({ isActive }) =>
					cs("nav-item__link", "nav-item__link--left", {
						"nav-item__link--active": isActive
					})
				}
				to={left.to}
				title={left.text}
			>
				<div className="nav-item__icon">{left.icon}</div>
				<div className="nav-item__text">{left.text}</div>
			</NavItemLink>
			{right !== undefined && (
				<NavItemLink
					useBare={useBare}
					className={({ isActive }) =>
						cs("nav-item__link", "nav-item__link--right", {
							"nav-item__link--active": isActive
						})
					}
					to={right.to}
					title={right.text}
				>
					{right.icon}
				</NavItemLink>
			)}
			<div className="nav-item__decorator nav-item__decorator--top" />
			<div className="nav-item__decorator nav-item__decorator--bottom" />
		</div>
	);
};

type NavLinkProps = React.ComponentProps<typeof NavLink>;
type OrgModLinkProps = React.ComponentProps<typeof OrgModLink>;

const NavItemLink: React.FunctionComponent<
	(NavLinkProps | OrgModLinkProps) & { useBare?: boolean }
> = ({ useBare, ...props }) =>
	useBare === true ? (
		<NavLink {...(props as NavLinkProps)} />
	) : (
		<OrgModLink {...(props as OrgModLinkProps)} />
	);
